"use client";

import { useCallback, type ReactNode, type ElementType, type MouseEvent } from "react";
import { useEditMode } from "@/contexts/EditModeContext";
import { Pencil } from "lucide-react";
import { cn } from "@/lib/shared/utils";

type EditableTextProps = {
  section: string;
  field: string;
  value: string;
  className?: string;
  as?: ElementType;
  children?: ReactNode;
  light?: boolean;
};

export default function EditableText({
  section,
  field,
  value,
  className,
  as: Tag = "span",
  children,
  light = false,
}: EditableTextProps) {
  const { isEditing, requestEdit } = useEditMode();

  const handleClick = useCallback(
    (e: MouseEvent) => {
      if (!isEditing) return;
      e.preventDefault();
      e.stopPropagation();
      const el = e.currentTarget as HTMLElement;
      const rect = el.getBoundingClientRect();
      requestEdit({ section, field, value, rect });
    },
    [isEditing, section, field, value, requestEdit]
  );

  if (!isEditing) {
    return <Tag className={className}>{children ?? value}</Tag>;
  }

  return (
    <Tag
      className={cn(
        className,
        "relative cursor-pointer",
        light
          ? "bg-background/5 outline-background/40 hover:bg-background/10 hover:outline-background/70 outline outline-2 outline-offset-2 outline-dashed"
          : "outline-foreground/40 bg-foreground/5 hover:outline-foreground/70 hover:bg-foreground/10 outline outline-2 outline-offset-2 outline-dashed",
        "transition-all"
      )}
      onClick={handleClick}
    >
      {children ?? value}
      <Pencil
        size={10}
        className={cn(
          "absolute -top-1.5 -right-1.5",
          light ? "text-background/50" : "text-foreground/50"
        )}
        strokeWidth={2.5}
      />
    </Tag>
  );
}
